import { useState, useMemo, Fragment } from "react";
import { colones } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight } from "lucide-react";

const PROPIOS_KEY = "__propios__";

function agrupar(lineas) {
  const orden = [];
  const map = new Map();
  for (const m of lineas) {
    const key = m.heredado_de || PROPIOS_KEY;
    if (!map.has(key)) {
      const isPropios = key === PROPIOS_KEY;
      map.set(key, { key, label: isPropios ? "Mano de obra propia" : `Heredado de: ${m.heredado_de}`,
                     isPropios, lineas: [] });
      orden.push(key);
    }
    map.get(key).lineas.push(m);
  }
  return orden.map((k) => map.get(k));
}

export const ManoObraSection = ({ manoObra, total, onAgregar, onEditar, onEliminar }) => {
  const grupos = useMemo(() => agrupar(manoObra), [manoObra]);
  const [colapsados, setColapsados] = useState(new Set());
  const hayHerencias = grupos.some((g) => !g.isPropios);

  const toggle = (key) => setColapsados((s) => {
    const n = new Set(s);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });

  const eliminarGrupo = async (grupo) => {
    if (!window.confirm(`¿Eliminar las ${grupo.lineas.length} línea(s) de mano de obra del grupo "${grupo.label}"?`)) return;
    for (const m of grupo.lineas) await onEliminar(m.id);
  };

  return (
    <div className="bg-white border border-border rounded-sm">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <h2 className="font-heading font-bold text-sm uppercase">Mano de obra</h2>
        <Button data-testid="agregar-mano-obra-btn" variant="outline" size="sm" className="no-print" onClick={onAgregar}>
          <Plus size={13} className="mr-1" /> Agregar
        </Button>
      </div>
      {manoObra.length === 0 && (
        <p className="px-3 py-4 text-sm text-muted-foreground">Sin líneas de mano de obra. Se agregan al cargar plantillas o manualmente.</p>
      )}
      {grupos.map((g) => {
        const oculto = colapsados.has(g.key);
        const subtotal = g.lineas.reduce((s, m) => s + (m.monto || 0), 0);
        return (
          <Fragment key={g.key}>
            {hayHerencias && (
              <div data-testid={`mo-grupo-header-${g.key}`}
                   className={`cot-group-header flex items-center gap-2 px-3 py-1.5 border-b border-border/60 ${g.isPropios ? "bg-zinc-50" : "bg-amber-50"}`}>
                <button className="no-print flex items-center gap-1 text-xs font-heading uppercase font-semibold"
                  data-testid={`toggle-mo-grupo-${g.key}`} onClick={() => toggle(g.key)}>
                  {oculto ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                  {g.label}
                  <span className="text-muted-foreground normal-case font-normal">({g.lineas.length})</span>
                </button>
                <span className="hidden print:inline text-xs font-heading uppercase font-semibold">{g.label} ({g.lineas.length})</span>
                <span className="ml-auto text-xs font-semibold">{colones(subtotal)}</span>
                <div className="no-print">
                  <Button data-testid={`eliminar-mo-grupo-${g.key}`} variant="ghost" size="icon"
                    className="h-6 w-6 text-destructive" title="Eliminar todo el grupo"
                    onClick={() => eliminarGrupo(g)}>
                    <Trash2 size={12} />
                  </Button>
                </div>
              </div>
            )}
            {g.lineas.map((m) => (
              <div key={m.id} data-testid={`mano-obra-${m.id}`}
                   className={`flex items-center justify-between px-3 py-2 border-b border-border/60 text-sm ${oculto ? "hidden cot-row-hidden" : ""}`}>
                <span className="flex-1 min-w-0 truncate">{m.descripcion}
                  {m.heredado_de && (
                    <span className="ml-1.5 text-[9px] bg-amber-100 text-amber-800 px-1 py-0.5 rounded-sm uppercase font-semibold" title={`Heredado de: ${m.heredado_de}`}>
                      Heredado
                    </span>
                  )}
                </span>
                <span className="font-semibold mr-2">
                  {m.frt ? <span className="text-[10px] text-muted-foreground font-normal mr-1.5" data-testid={`mo-frt-${m.id}`}>{m.frt} h × {colones(m.tarifa || 0)}</span> : null}
                  {colones(m.monto)}
                </span>
                <div className="flex gap-1 no-print">
                  <Button data-testid={`editar-mo-${m.id}`} variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEditar(m)}><Pencil size={13} /></Button>
                  <Button data-testid={`eliminar-mo-${m.id}`} variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onEliminar(m.id)}><Trash2 size={13} /></Button>
                </div>
              </div>
            ))}
          </Fragment>
        );
      })}
      <div className="flex items-center justify-between px-3 py-2.5 bg-accent">
        <span className="font-heading font-bold text-sm uppercase">Total mano de obra</span>
        <span className="font-heading font-bold text-lg" data-testid="total-mano-obra">{colones(total)}</span>
      </div>
    </div>
  );
};
